import { StorageAdapter } from '../storage';
import { uuid } from '../uuid/uuid';

import type { RawSession } from './types';

declare global {
  interface Window {
    legacyMode?: boolean;
  }
}

export class SessionManager {
  static readonly coreSessionIdKey = 'coreSessionId';
  static readonly coreBrowserIdKey = 'coreBrowserId';

  readonly coreSessionId: string;
  readonly coreBrowserId: string;

  readonly sessionKey: string;

  readonly preserveUrlSession: boolean;

  readonly session: RawSession | null;

  protected windowObject: Window;

  protected resolveSessionKey(): string {
    return this.windowObject.name === 'lobby_iframe' ? 'PPG_L' : 'PPG';
  }

  protected resolveWindowObject(): Window {
    return window.legacyMode ? window.parent : window;
  }

  constructor(
    private sessionStorage: StorageAdapter,
    private localStorage: StorageAdapter,
    private options?: {
      windowObject?: Window;
      preserveUrlSession?: boolean;
      sessionKey?: string;
    }
  ) {
    // Resolve options
    this.windowObject = this.options?.windowObject ?? this.resolveWindowObject();
    this.sessionKey = this.options?.sessionKey ?? this.resolveSessionKey();

    // Init session
    this.session = this.getSession();

    this.preserveUrlSession = this.options?.preserveUrlSession ?? this.session?.preserveUrlSession === 'true';

    // Stash Session
    if (this.session) {
      this.stashSession(this.session);
    }

    // Init session id
    this.coreSessionId = this.getSessionId();

    // Init browser id
    this.coreBrowserId = this.getId(SessionManager.coreBrowserIdKey, this.localStorage);
  }

  private getSessionId(): string {
    let id = '';
    const JSESSIONID = this.session?.['JSESSIONID'];
    if (JSESSIONID) {
      id = btoa(JSESSIONID).substring(0, 32);
      this.sessionStorage.set(SessionManager.coreSessionIdKey, id);
    }
    if (id) return id;
    return this.getId(SessionManager.coreSessionIdKey, this.sessionStorage);
  }

  private getId(key: string, storageAdapter: StorageAdapter): string {
    let id = storageAdapter.get(key);
    if (!id) {
      id = uuid.v4();
    }
    storageAdapter.set(key, id);
    return id;
  }

  getSession(): RawSession | null {
    if (this.session) return this.session;
    const rawSession = this.getSessionFromUrl() || this.getSessionFromHistory() || this.getStoredSession();
    if (!rawSession) return null;
    return rawSession;
  }

  getSessionFromUrl(): RawSession | null {
    if (!this.windowObject.location.search) return null;
    const sessionResult: RawSession = {};
    const searchParams = new URLSearchParams(this.windowObject.location.search);
    searchParams.forEach((value, key) => {
      sessionResult[key] = value === 'null' ? '' : value;
    });
    sessionResult.gamePath = this.windowObject.location.pathname.replace(/\/+$/, '');
    return sessionResult;
  }

  getSessionFromHistory(): RawSession | null {
    return this.windowObject.history.state as RawSession;
  }

  getStoredSession(): RawSession | null {
    const storedSession = this.sessionStorage.getJson<RawSession>(this.sessionKey);
    return storedSession ?? null;
  }

  get(key: string): string | undefined {
    return this.session?.[key];
  }

  stashSession(session: RawSession): void {
    if (this.windowObject.location.search) {
      if (!this.preserveUrlSession) {
        this.sessionStorage.setJson(this.sessionKey, session);
        this.windowObject.history.replaceState(session, '', this.windowObject.location.pathname);
      }
    }
  }
}
